import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { combineLatest, filter, map, Observable, of, take } from 'rxjs';

import { APP_ROUTES } from '@ps/core-config';

import {
  selectRootSurvey,
  selectRootSurveyError,
  selectRootSurveyId,
} from '../state/core/survey-creation-core.selectors';
import { SurveyCreationActionsFacade } from '../state/survey-creation.actions.facade';
import { parseSurveyId, surveyLoadOutcome } from './domain/survey-load-outcome';

/**
 * Loads the survey named by the route's `:id` param into the survey-creation
 * state before the route activates, so the page renders with the survey
 * already in the store.
 *
 * Gate only: the survey is read from the store, never from route data. A bad
 * id or a failed load redirects to Not Found. Dispatching `init` is always
 * safe — the reducer treats a repeat as a no-op. The page host clears the
 * state on destroy and `init`s again on init, because the outgoing page is
 * destroyed after this resolver has run.
 */
export const surveyResolver: ResolveFn<boolean> = (route) => {
  const router = inject(Router);
  const store = inject(Store);
  const actions = inject(SurveyCreationActionsFacade);

  const notFound = (): RedirectCommand =>
    new RedirectCommand(router.parseUrl(APP_ROUTES.NOT_FOUND));

  const awaitLoad = (
    surveyId: number,
  ): Observable<boolean | RedirectCommand> => {
    actions.initAudience(surveyId);

    return combineLatest([
      store.select(selectRootSurveyId),
      store.select(selectRootSurvey),
      store.select(selectRootSurveyError),
    ]).pipe(
      map(([rootSurveyId, survey, error]) =>
        surveyLoadOutcome(surveyId, {
          rootSurveyId,
          survey: Option.from(survey),
          error,
        }),
      ),
      filter((outcome) => outcome.isSome()),
      take(1),
      map((outcome) =>
        outcome.unwrap().match({
          ok: () => true,
          err: () => notFound(),
        }),
      ),
    );
  };

  return parseSurveyId(route.paramMap.get('id')).match({
    ok: awaitLoad,
    err: () => of(notFound()),
  });
};
